/*
 * Player invite status — derived from the EventPlayer row.
 *
 * The DB has a 'status' column (invited/active/declined/removed) but it
 * doesn't cleanly capture the "has this person seen and accepted their
 * invite?" question captains actually care about. We derive a four-state
 * status from the existing fields so we don't need a schema migration:
 *
 *   joined        userId is set — they've either claimed a guest slot or
 *                 were added directly as an app user. The captain doesn't
 *                 need to do anything else.
 *   pending       userId is null but email or phone is set — an invite
 *                 has been sent (or at minimum the contact is on file)
 *                 and we're waiting for them to claim.
 *   not_invited   No contact info on the row. The captain hasn't sent
 *                 anything yet. This is the actionable state.
 *   declined      status === 'declined' on the row. Rare but supported.
 *
 * Owners of the event are treated as 'joined' since they're always in
 * the event. The roster page filters out the owner-mirror row anyway,
 * so this case is mostly defensive.
 */

import type { EventPlayer } from '@golf-app/shared';

export type InviteStatus = 'joined' | 'pending' | 'not_invited' | 'declined';

export function getInviteStatus(p: EventPlayer): InviteStatus {
  if (p.status === 'declined') return 'declined';
  if (p.userId) return 'joined';
  if (p.email || p.phone) return 'pending';
  return 'not_invited';
}

export function inviteStatusLabel(s: InviteStatus): string {
  switch (s) {
    case 'joined':      return 'Account linked';
    case 'pending':     return 'Invite pending';
    case 'not_invited': return 'Not invited';
    case 'declined':    return 'Declined';
  }
}

/** Tailored CSS-variable colors for each state. Pulls from the existing
 *  theme tokens so we don't introduce new colors. */
export function inviteStatusColor(s: InviteStatus): string {
  switch (s) {
    case 'joined':      return 'var(--green-bright)';
    case 'pending':     return 'var(--gold)';
    case 'not_invited': return 'var(--text-mut)';
    case 'declined':    return '#ef4444'; // red-500
  }
}
